import { TopicRecord } from '@playbook/data-models/records/topic-record';
import { ExternalApiDataPublicationList } from '@src/suite/cast/data-models/service/external-api-data-publication-list';
import { Address } from '@src/suite/common/data-models/complex-types/address';
import { AttachmentList } from '@src/suite/common/data-models/complex-types/attachment-list';
import { DateRange } from '@src/suite/common/data-models/complex-types/date-range';
import { DateTime } from '@src/suite/common/data-models/complex-types/date-time';
import { LookupList } from '@src/suite/common/data-models/complex-types/lookup-list';
import { MultiValue } from '@src/suite/common/data-models/complex-types/multi-value';
import { Path } from '@src/suite/common/data-models/complex-types/path';
import { Table } from '@src/suite/common/data-models/complex-types/table';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { TileColumn } from '@src/suite/common/data-models/misc/tile-column';
import { TileDay } from '@src/suite/common/data-models/misc/tile-day';
import { ViewStateDelay } from '@src/suite/common/data-models/navigation/viewstate-delay';
import { ViewStateFilter } from '@src/suite/common/data-models/navigation/viewstate-filter';
import { ViewStateOptions } from '@src/suite/common/data-models/navigation/viewstate-options';
import { ViewStateSequence } from '@src/suite/common/data-models/navigation/viewstate-sequence';
import { Record } from '@src/suite/common/data-models/records/record';
import { GroupKey } from '@src/suite/common/data-models/search-results/group-key';
import { ExternalApiData } from '@src/suite/common/data-models/service/external-api-data';
import { FieldDefValidations } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';

import { Util } from './util';

export const getExternalApiData = (actionName: string, userId: string, rec: StorytellingRecord): ExternalApiData => {
    return new ExternalApiData(actionName, rec.Database, rec.Path.fullPath, rec.Guid, userId);
};

export const getPublicationExternalApiData = (
    actionName: string,
    userId: string,
    rec: StorytellingRecord,
    channelSections?: { [id: string]: Array<StorytellingPublicationRecord> }
): ExternalApiData => {
    return new ExternalApiDataPublicationList(actionName, rec.Database, rec.Path.fullPath, rec.Guid, userId, rec, channelSections);
};

// TODO This should go in the full screen component as a getter most likely. We will see
const resetPropertyGroup = (rec: StorytellingRecord) => {
    const newGroups = { ...rec.requiredFieldsGroups };
    for (const group in newGroups) {
        newGroups[group] = true;
    }
    return newGroups;
};
// TODO This should go in the full screen component as a getter most likely. We will see
export const checkRequiredProperties = (rec: StorytellingRecord) => {
    // Slug is always required
    if ((!rec.Slug || !rec.Slug.trim()) && (!rec.Title || !rec.Title.trim())) {
        return false;
    }
    if (Record.globalTablesService.EnforceRequiredFieldsOnCreate) {
        const fields: { [id: string]: FieldDef } = getRequiredProperties(rec);
        for (const field in fields) {
            const populated = checkIfValueIsPopulated(field, rec);
            if (!populated) {
                return false;
            }
        }
    }
    return true;
};

export const calculateRequiredFieldGroups = (rec: StorytellingRecord) => {
    const newGroups = resetPropertyGroup(rec);
    if (Record.globalTablesService.EnforceRequiredFieldsOnCreate) {
        const fields: { [id: string]: FieldDef } = getRequiredProperties(rec);

        for (const field in fields) {
            const populated = checkIfValueIsPopulated(field, rec);

            if (!populated) {
                const item: FieldDef = Record.globalTablesService.FieldDefsDict[field];
                if (item && (item.Validation.toUpperCase() === 'SPECIAL' || item.Validation.toUpperCase() === 'COST')) {
                    newGroups[item.ID] = false;
                } else {
                    let group = Record.globalTablesService.propertiesGroupCache[field];
                    if (typeof group !== 'undefined') {
                        if (!group) group = '';
                        newGroups[group] = false;
                    }
                }
            }
        }
    }
    return newGroups;
};

export const getDataTransferString = (rec: StorytellingRecord): string => {
    const ret = new Array<{ FieldName: string; FieldValue: any }>();
    ret.push({ FieldName: 'Database', FieldValue: rec.Database });
    ret.push({ FieldName: 'Path', FieldValue: rec.Path ? rec.Path.fullPath : '' });
    ret.push({ FieldName: 'Guid', FieldValue: rec.Guid });
    ret.push({ FieldName: 'Type', FieldValue: rec.Type });
    return JSON.stringify(ret);
};

export const getRequiredProperties = (rec: StorytellingRecord): { [id: string]: FieldDef } => {
    // TODO Fix this
    switch (rec.Type) {
        case ObjectTypes.TOPIC:
            return TopicRecord.playbookService.TopicRequiredFieldDefsDict;
        case ObjectTypes.ASSIGNMENT:
            return TopicRecord.playbookService.AssignmentRequiredFieldDefsDict;
        case ObjectTypes.SCRIPT:
            return TopicRecord.playbookService.StoryRequiredFieldDefsDict;
        default:
            return {};
    }
};
export const initializeRecord = <T extends StorytellingRecord>(type: number, templateRecord: StorytellingRecord = {}): T => {
    const rec = templateRecord || ({} as StorytellingRecord);
    if (type) {
        rec.Type = type;
    }

    initializeVisibilityCriteria(rec);
    // rec.ModTime = new DateTime(new Date());
    switch (type) {
        case ObjectTypes.TOPIC: {
            setupRequiredFieldsGroups('Topic', rec);
            rec.requiredFieldsGroups.Assignments = true;
            rec.requiredFieldsGroups.Script = true;
            return rec as T;
        }
        case ObjectTypes.ASSIGNMENT: {
            setupRequiredFieldsGroups('Assignment', rec);
            return rec as T;
        }
        case ObjectTypes.UNASSIGNED:
        case ObjectTypes.SCRIPT: {
            setupRequiredFieldsGroups('Script', rec);
            return rec as T;
        }
        case ObjectTypes.RUNDOWN: {
            setupRequiredFieldsGroups('Rundown', rec);
            return rec as T;
        }
        case ObjectTypes.PUBLICATIONLIST: {
            setupRequiredFieldsGroups('PublicationList', rec);
            return rec as T;
        }
        case ObjectTypes.PUBLICATION: {
            setupRequiredFieldsGroups('Publication', rec);
            return rec as T;
        }
        case ObjectTypes.PUBLICATION_CHANNEL: {
            setupRequiredFieldsGroups('PublicationChannel', rec);
            return rec as T;
        }
        case ObjectTypes.USER: {
            return rec as T;
        }
        case ObjectTypes.EXPORTFORMAT: {
            return rec as T;
        }
        case ObjectTypes.DISCOVER_ITEM: {
            return rec as T;
        }
        case ObjectTypes.DISCOVER_WIRE: {
            return rec as T;
        }
        default: {
            //console.log('Unknown type: ' + type);
            //console.log('Unknown type: ' + JSON.stringify(rec));
            return rec as T;
        }
    }
};

export const initializeViewStateRecord = <T extends StorytellingViewStateRecord>(viewStateType: number): T => {
    const rec = {} as StorytellingViewStateRecord;
    rec.Type = ObjectTypes.PSTVIEWSTATE;
    rec.PSTViewStateType = viewStateType;
    switch (viewStateType) {
        case ViewStateTypes.SYSTEM: {
            return rec as T;
        }
        case ViewStateTypes.TOPIC: {
            return rec as T;
        }
        case ViewStateTypes.ASSIGNMENT: {
            return rec as T;
        }
        case ViewStateTypes.SEARCH: {
            return rec as T;
        }
        case ViewStateTypes.SINGLEITEM: {
            return rec as T;
        }
        case ViewStateTypes.DRAFT: {
            return rec as T;
        }
        case ViewStateTypes.DRAFTSEARCH: {
            return rec as T;
        }
        case ViewStateTypes.WIRE: {
            return rec as T;
        }
        case ViewStateTypes.WIRESEARCH: {
            return rec as T;
        }
        case ViewStateTypes.ROLIST: {
            return rec as T;
        }
        case ViewStateTypes.PUBLICATION: {
            return rec as T;
        }
        case ViewStateTypes.MULTIPLEFEED: {
            return rec as T;
        }
        case ViewStateTypes.RSSFEED: {
            return rec as T;
        }
        case ViewStateTypes.TWITTERFEED: {
            return rec as T;
        }
        case ViewStateTypes.WIREFEED: {
            return rec as T;
        }
        case ViewStateTypes.APFEED: {
            return rec as T;
        }
        case ViewStateTypes.FTPFEED: {
            return rec as T;
        }
        default: {
            return rec as T;
        }
    }
};

export const cleanupRecord = <T extends StorytellingRecord>(rec: T): T => {
    const newRec = { ...rec };
    delete newRec.visibilityCriteria;
    delete newRec.isNewRecord;
    delete newRec.readOnlyCriteria;
    delete newRec.isRequiredFieldsPopulated;
    delete newRec.requiredFieldsGroups;
    delete newRec.visibilityCriteriaGroups;

    return cleanupCollectionRecord(newRec);
};

export const cleanupCollectionRecord = <T extends StorytellingCollectionRecord>(rec: T): T => {
    const newRec = { ...rec };

    delete newRec.children;
    delete newRec.stories;
    delete newRec.Sequence;
    return newRec;
};

const initializeVisibilityCriteria = (rec: StorytellingRecord) => {
    rec.visibilityCriteria = {};
    rec.readOnlyCriteria = {};
    rec.visibilityCriteriaGroups = {};
    const excluded = recordExcludedFieldsByType(rec.Type);
    for (const key in Record.allPropertiesCriteriaCache[rec.Type]) {
        const prop = Record.allPropertiesCriteriaCache[rec.Type][key];
        if (!excluded[prop.ID]) {
            rec.visibilityCriteria[prop.ID] = true;
            rec.readOnlyCriteria[prop.ID] = false;
            let group = prop.FieldGroup?.trim() || '';
            if (group?.toUpperCase() === Record.globalTablesService.PropertiesGroupKey) {
                group = '';
            }
            if (!rec.visibilityCriteriaGroups[group]) rec.visibilityCriteriaGroups[group] = {};
            rec.visibilityCriteriaGroups[group][prop.ID] = prop;
        }
    }

    for (const key in Record.allActionsCriteriaCache) {
        const action = Record.allActionsCriteriaCache[key];
        if ((action.TopicAction || action.AssignmentAction || action.ScriptAction) && !action.VisibilityCriteria) {
            rec.visibilityCriteria[action.ID] = true;
        }
        if (!action.TopicAction && !action.AssignmentAction && !action.ScriptAction) {
            rec.visibilityCriteria[action.ID] = false;
        }
    }
};

const recordExcludedFieldsByType = (type: number): { [id: string]: boolean } => {
    const excluded: { [id: string]: boolean } = {};
    switch (type) {
        case ObjectTypes.TOPIC:
            excluded[PSTFieldNames.Title] = true;
            excluded[PSTFieldNames.PSTDesk] = true;
            excluded[PSTFieldNames.DescriptionType] = true;
            excluded[PSTFieldNames.PSTAssignmentCost] = true;
            excluded[PSTFieldNames.PSTTags] = true;
            excluded[PSTFieldNames.PSTAssignmentStatus] = true;
            excluded[PSTFieldNames.Description] = true;
            excluded[PSTFieldNames.PSTExternalFiles] = true;
            excluded[PSTFieldNames.PSTApproved] = true;
            return excluded;
        case ObjectTypes.ASSIGNMENT:
            excluded[PSTFieldNames.Title] = true;
            excluded[PSTFieldNames.Description] = true;
            excluded[PSTFieldNames.PSTDesk] = true;
            excluded[PSTFieldNames.PSTTags] = true;
            excluded[PSTFieldNames.PSTTopicStatus] = true;
            excluded[PSTFieldNames.PSTTopicType] = true;
            excluded[PSTFieldNames.PSTTopicStoryOwner] = true;
            excluded[PSTFieldNames.Owner] = true;
            excluded[PSTFieldNames.PSTExternalFiles] = true;
            excluded[PSTFieldNames.PSTApproved] = true;
            return excluded;
        case ObjectTypes.SCRIPT:
            excluded[PSTFieldNames.Title] = true;
            excluded[PSTFieldNames.PSTDesk] = true;
            excluded[PSTFieldNames.PSTTags] = true;
            excluded[PSTFieldNames.PSTStoryStatus] = true;
            excluded[PSTFieldNames.PSTStoryOutputType] = true;
            excluded[PSTFieldNames.PSTStoryOwner] = true;
            excluded[PSTFieldNames.Description] = true;
            excluded[PSTFieldNames.PSTApproved] = true;
            return excluded;
        default:
            return {};
    }
};

export const checkIfValueIsPopulated = (key: any, rec: StorytellingRecord): boolean => {
    const v = rec[key];

    if (typeof rec.visibilityCriteria[key] !== 'undefined' && !rec.visibilityCriteria[key]) {
        return true;
    }

    if (!v || v.length === 0) {
        return false;
    }
    if (typeof v === 'string') {
        if (v.trim().length === 0) {
            return false;
        }
    }
    if (v instanceof LookupList) {
        if (!v.hasValues()) {
            return false;
        }
    }
    if (v instanceof MultiValue) {
        if (!v.hasValues()) {
            return false;
        }
    }
    if (v instanceof DateTime) {
        if (!v.get() || v.get().getUTCFullYear() === 1800) {
            return false;
        }
    }
    if (v instanceof Address) {
        if (!v.lat || !v.long) {
            return false;
        }
    }
    if (v instanceof Table) {
        if (!v.hasValues()) {
            return false;
        }
    }
    return true;
};

export const updateVisibility = (rec: StorytellingRecord, _initial: boolean = false) => {
    let visible = false;
    // TODO Think how we can optimize this
    const visibilityCriteria = { ...rec.visibilityCriteria };
    const visibilityCriteriaGroups = { ...rec.visibilityCriteriaGroups };
    if (Record.allPropertiesCriteriaCache[rec?.Type]) {
        const keys = Object.values(Record.allPropertiesCriteriaCache[rec?.Type]).map(prop => prop?.ID);
        for (const key of keys) {
            const propsToUpdate = Record.visibilityCriteriaReverseCache[key];
            if (propsToUpdate) {
                for (const prop of propsToUpdate) {
                    if (Record.visibilityCriteriaCache[prop]) {
                        try {
                            if (Object.keys(rec).length > 0) {
                                visible = eval(Record.visibilityCriteriaCache[prop].criteria);
                            }
                        } catch (e) {
                            visible = false;
                            // console.log("Visibility Criteria for field " + prop + " is not a valid expression.")
                        }
                    } else {
                        visible = true;
                    }
                    try {
                        visibilityCriteria[prop] = visible;
                        if (Record.allPropertiesCriteriaCache[rec.Type] && Record.allPropertiesCriteriaCache[rec.Type][prop]) {
                            let propGroup = Record.allPropertiesCriteriaCache[rec.Type][prop]?.FieldGroup?.trim() || '';
                            if (propGroup.toUpperCase() === Record.globalTablesService.PropertiesGroupKey) {
                                propGroup = '';
                            }
                            if (visible) {
                                if (!visibilityCriteriaGroups[propGroup]) visibilityCriteriaGroups[propGroup] = {};
                                visibilityCriteriaGroups[propGroup][prop] = Record.allPropertiesCriteriaCache[rec.Type][prop];
                            } else if (visibilityCriteriaGroups[propGroup]) {
                                delete visibilityCriteriaGroups[propGroup][prop];
                                if (Object.keys(visibilityCriteriaGroups[propGroup]).length === 0) {
                                    delete visibilityCriteriaGroups[propGroup];
                                }
                            }
                        }
                    } catch (_error) {
                        // empty catch block
                        // console.error('Error updating visibility for property: ' + prop);
                    }
                }
            }
        }
    }
    rec.visibilityCriteria = visibilityCriteria;
    rec.visibilityCriteriaGroups = visibilityCriteriaGroups;
    // }
};

export const updateReadOnlyCriteria = (rec: StorytellingRecord, _initial: boolean = false) => {
    let readOnly = false;
    // TODO Think how we can optimize this
    const readOnlyCriteria = { ...rec.readOnlyCriteria };
    if (Record.allPropertiesCriteriaCache[rec?.Type]) {
        const keys = Object.values(Record.allPropertiesCriteriaCache[rec?.Type]).map(prop => prop?.ID);
        for (const key of keys) {
            const propsToUpdate = Record.readOnlyCriteriaReverseCache[key];
            if (propsToUpdate) {
                for (const prop of propsToUpdate) {
                    if (Record.readOnlyCriteriaCache[prop]) {
                        try {
                            if (Object.keys(rec).length > 0) {
                                readOnly = eval(Record.readOnlyCriteriaCache[prop].criteria);
                            }
                        } catch (e) {
                            readOnly = false;
                            // console.log("Visibility Criteria for field " + prop + " is not a valid expression.")
                        }
                    } else {
                        readOnly = false;
                    }
                    try {
                        readOnlyCriteria[prop] = readOnly;
                    } catch (_error) {
                        // empty catch block
                        // console.error('Error updating ReadOnlyCriteria for property: ' + prop);
                    }
                }
            }
        }
    }
    rec.readOnlyCriteria = readOnlyCriteria;
    // }
};

export const setupRequiredFieldsGroups = (type: string, rec: StorytellingRecord) => {
    rec.requiredFieldsGroups = {};
    const groups = Record.globalTablesService.getFieldGroupsFor(type);
    for (const group of groups) {
        rec.requiredFieldsGroups[group] = true;
        // Make sure cache is populated
        Record.globalTablesService.getDynamicPropertiesFor(type, group);
    }
    const specials = Record.globalTablesService.getSpecialPropertiesFor(type);
    for (const special of specials) {
        rec.requiredFieldsGroups[special.ID] = true;
    }
};

export const getTopicSecurityPrivsWrite = (rec: StorytellingTopicRecord): boolean => {
    const desk = rec?.PSTGroup || [];
    let ret = false;
    if (desk.length > 0) {
        for (const val of desk) {
            if (Record.globalTablesService.securityPrivileges[val]) {
                if (Record.globalTablesService.securityPrivileges[val].topic.write) {
                    ret = true;
                }
            } else {
                ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.write;
            }
        }
        return ret;
    }
    return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.write;
};

export const getTopicSecurityPrivsRead = (rec: StorytellingTopicRecord): boolean => {
    const desk = rec?.PSTGroup || [];
    let ret = false;
    if (desk.length > 0) {
        for (const val of desk) {
            if (Record.globalTablesService.securityPrivileges[val]) {
                if (Record.globalTablesService.securityPrivileges[val].topic.read) {
                    ret = true;
                }
            } else {
                ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.read;
            }
        }
        return ret;
    }
    return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.read;
};

export const getTopicChildrenSecurityPrivsWrite = (rec: StorytellingTopicRecord): boolean => {
    const desk = rec?.PSTGroup || [];
    let ret = false;
    if (desk.length > 0) {
        for (const val of desk) {
            if (Record.globalTablesService.securityPrivileges[val]) {
                if (Record.globalTablesService.securityPrivileges[val].assignment.write) {
                    ret = true;
                }
            } else {
                ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
            }
        }
        return ret;
    }
    return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
};

export const getAssignmentSecurityPrivsWrite = (rec: StorytellingAssignmentRecord): boolean => {
    const desk = rec?.PSTGroup || [];
    let ret = false;
    if (desk.length > 0) {
        for (const val of desk) {
            if (Record.globalTablesService.securityPrivileges[val]) {
                if (Record.globalTablesService.securityPrivileges[val].assignment.write) {
                    ret = true;
                }
            } else {
                ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
            }
        }
        return ret;
    }
    return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
};

export const getAssignmentSecurityPrivsRead = (rec: StorytellingAssignmentRecord): boolean => {
    const desk = rec?.PSTGroup || [];
    let ret = false;
    if (desk.length > 0) {
        for (const val of desk) {
            if (Record.globalTablesService.securityPrivileges[val]) {
                if (Record.globalTablesService.securityPrivileges[val].assignment.read) {
                    ret = true;
                }
            } else {
                ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
            }
        }
        return ret;
    }
    return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
};

export const getStorySecurityPrivsWrite = (_rec: StorytellingStoryRecord): boolean => {
    // TODO: Implement This
    // const desk = rec?.PSTGroup || [];
    // let ret = false;
    // if (desk.length > 0) {
    //     for (const val of desk) {
    //         if (Record.globalTablesService.securityPrivileges[val]) {
    //             if (Record.globalTablesService.securityPrivileges[val].story.write) {
    //                 ret = true;
    //             }
    //         } else {
    //             ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].story.write;
    //         }
    //     }
    //     return ret;
    // }
    // return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].story.write;
    return true;
};

export const getStorySecurityPrivsRead = (_rec: StorytellingStoryRecord): boolean => {
    // TODO: Implement This
    // const desk = rec?.PSTGroup || [];
    // let ret = false;
    // if (desk.length > 0) {
    //     for (const val of desk) {
    //         if (Record.globalTablesService.securityPrivileges[val]) {
    //             if (Record.globalTablesService.securityPrivileges[val].story.read) {
    //                 ret = true;
    //             }
    //         } else {
    //             ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].story.read;
    //         }
    //     }
    //     return ret;
    // }
    // return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].story.read;
    return true;
};

export const getCostPrivsWrite = (rec: StorytellingAssignmentRecord | StorytellingTopicRecord | StorytellingStoryRecord): boolean => {
    const desk = rec.PSTGroup || [];
    let ret = false;
    if (desk.length > 0) {
        for (const val of desk) {
            if (Record.globalTablesService.securityPrivileges[val]) {
                if (Record.globalTablesService.securityPrivileges[val].cost.write) {
                    ret = true;
                }
            } else {
                ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].cost.write;
            }
        }
        return ret;
    }
    return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].cost.write;
};

export const getCostPrivsRead = (rec: StorytellingAssignmentRecord | StorytellingTopicRecord | StorytellingStoryRecord): boolean => {
    const desk = rec.PSTGroup || [];
    let ret = false;
    if (desk.length > 0) {
        for (const val of desk) {
            if (Record.globalTablesService.securityPrivileges[val]) {
                if (Record.globalTablesService.securityPrivileges[val].cost.read) {
                    ret = true;
                }
            } else {
                ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].cost.read;
            }
        }
        return ret;
    }
    return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].cost.read;
};

export const copyDataAndChildrenTo = (
    source: StorytellingCollectionRecord,
    target: StorytellingCollectionRecord,
    preserveGuids?: boolean,
    isTemplate?: boolean,
    fieldsToSkip?: { [field: string]: boolean }
) => {
    copyDataTo(source, target, preserveGuids, fieldsToSkip);
    if (isTemplate) {
        fieldsToSkip[PSTFieldNames.Title.toLowerCase()] = false;
    }
    for (const child of source.children) {
        const newChild = initializeRecord(ObjectTypes.ASSIGNMENT);
        newChild.Guid = undefined;
        copyDataTo(child, newChild, preserveGuids, fieldsToSkip);
        target.children = [...(target.children || []), newChild];
    }
};

export const copyDataTo = (source: StorytellingRecord, target: StorytellingRecord, preserveGuid?: boolean, fieldsToSkip?: { [field: string]: boolean }, _copyParentRef: boolean = false) => {
    for (const key in source) {
        if ((!preserveGuid && key.toLowerCase() === 'guid') || (fieldsToSkip && fieldsToSkip[key.toLowerCase()])) continue;
        // i++;
        if (!source[key]) continue;
        // TODO: Fix this later
        if (key === 'children') continue;

        const val = source[key];

        if (val instanceof MultiValue) {
            target[key] = <MultiValue<any>>val.deepCopy();
        } else if (val instanceof Address) {
            target[key] = <Address>val.deepCopy();
        } else if (val instanceof AttachmentList) {
            target[key] = <AttachmentList>val.deepCopy();
        } else if (val instanceof DateRange) {
            target[key] = <DateRange>val.deepCopy();
        } else if (val instanceof DateTime) {
            target[key] = <DateTime>val.deepCopy();
        } else if (val instanceof LookupList) {
            target[key] = <LookupList>val.deepCopy();
        } else if (val instanceof Table) {
            target[key] = <Table>val.deepCopy();
        } else if (val instanceof ViewStateFilter) {
            target[key] = <ViewStateFilter>val.deepCopy();
        } else if (val instanceof ViewStateOptions) {
            target[key] = <ViewStateOptions>val.deepCopy();
        } else if (val instanceof ViewStateDelay) {
            target[key] = <ViewStateDelay>val.deepCopy();
        } else if (val instanceof ViewStateSequence) {
            target[key] = <ViewStateSequence>val.deepCopy();
        } else if (val === null || isPrimitive(val) || val instanceof Date) {
            // (typeof val !== "object")
            // Primitive types
            target[key] = val;
        } else if (val instanceof Path) {
            target[key] = <Path>val.deepCopy();
        } else if (Array.isArray(val)) {
            target[key] = Util.deepCopy(val);
        } else if (!isPrimitive(val)) {
            // Object types
            target[key] = Util.deepCopy(val);
        }
    }

    // copy visibility criterira
    target.visibilityCriteria = {};
    for (const id in source.visibilityCriteria) {
        target.visibilityCriteria[id] = source.visibilityCriteria[id];
    }

    target.visibilityCriteriaGroups = {};
    for (const id in source.visibilityCriteriaGroups) {
        target.visibilityCriteriaGroups[id] = {};
        for (const fieldGroupId in source.visibilityCriteriaGroups[id]) {
            target.visibilityCriteriaGroups[id][fieldGroupId] = source.visibilityCriteriaGroups[id][fieldGroupId];
        }
    }

    // TODO Find out if needed
    // if (copyParentRef) {
    //     target.parentRecord = source.parentRecord;
    // }
};

export const isPrimitive = val => {
    return val !== Object(val);
};

export const isCollectionType = (type: number): boolean => {
    if (type === ObjectTypes.RUNDOWN || type === ObjectTypes.NGGRID || type === ObjectTypes.STORYBOARD || type === ObjectTypes.TOPIC || type === ObjectTypes.PUBLICATIONLIST) {
        return true;
    }
    return false;
};

export const checkNoValue = (value: any): boolean => {
    if (value instanceof MultiValue) {
        return !value.hasValues();
    }
    if (value instanceof DateTime) {
        return !value.get();
    }
    if (value === null || value === 'undefined' || value.toLowerCase() === 'no value' || value.toLowerCase() === 'null') {
        return true;
    }
    return false;
};

export const getTopicFieldsToUpdate = (
    rec: StorytellingAssignmentRecord | StorytellingTopicRecord,
    groupByField: FieldDef,
    targetGroup: any,
    isTimeField: boolean = false
): Array<{ key: string; value: any }> => {
    const data: Array<{ key: string; value: any }> = [];
    const groupByKeyValue: { key: string; value: any } = {
        key: groupByField.ID,
        value: rec[groupByField.ID],
    };
    data.push({
        key: PSTFieldNames.Type,
        value: rec.Type,
    });

    if (rec.Type === ObjectTypes.TOPIC || rec.Type === ObjectTypes.ASSIGNMENT) {
        const validation = groupByField.Validation.toUpperCase();
        switch (validation) {
            case FieldDefValidations.TEXT:
            case FieldDefValidations.COMBOBOX:
            case FieldDefValidations.CHECKBOX:
            case FieldDefValidations.TREE: {
                if (checkNoValue(targetGroup)) {
                    groupByKeyValue.value = '';
                } else {
                    groupByKeyValue.value = targetGroup;
                }

                break;
            }
            case FieldDefValidations.CHECKLISTEDIT:
            case FieldDefValidations.STAFFIDLIST:
            case FieldDefValidations.CHECKLIST: {
                const prop = new MultiValue<string>();
                prop.set(targetGroup);
                if (checkNoValue(targetGroup)) {
                    prop.clear();
                }
                groupByKeyValue.value = prop;
                break;
            }
            case FieldDefValidations.DATE:
            case FieldDefValidations.TIME: {
                let newDate = null;
                if (targetGroup instanceof DateTime) {
                    newDate = targetGroup.deepCopy();
                } else {
                    newDate = new DateTime(targetGroup);
                }
                if (checkNoValue(targetGroup)) {
                    newDate = null;
                }

                // TODO: handle the case when there is an hour in the targetGroup
                const recDate = rec[groupByField.ID] as DateTime;
                if (recDate) {
                    const recDateCopy = recDate.deepCopy();
                    recDateCopy.moment.year(newDate.moment.year());
                    recDateCopy.moment.month(newDate.moment.month());
                    recDateCopy.moment.date(newDate.moment.date());
                    // TODO: ask AP should we just group by hours? or group by hours and minutes and seconds
                    if (isTimeField) {
                        recDateCopy.moment.hour(newDate.moment.hour());
                    }
                    groupByKeyValue.value = recDateCopy;
                } else {
                    // TODO: take in mind the timezone
                    groupByKeyValue.value = newDate;
                }
                break;
            }
        }

        data.push(groupByKeyValue);
    } else {
        data.push({
            key: groupByField.ID,
            value: rec[groupByField.ID],
        });
    }
    return data;
};

export const createTopicInGroup = (rec: StorytellingAssignmentRecord | StorytellingTopicRecord, groupByField: FieldDef, column: GroupKey | TileDay | TileColumn) => {
    const fieldsToUpdate = getTopicFieldsToUpdate(rec, groupByField, column.value);
    for (const field of fieldsToUpdate) {
        rec[field.key] = field.value;
    }
};
